<script setup lang="ts">
const containerRef = useTemplateRef("containerRef");

const { minScale, maxScale } = defineProps<{
  minScale: number;
  maxScale: number;
}>();

const ZOOM_SPEED = 0.2;
const HOVER_THROTTLE_MS = 10;
const RESET_ANIMATION_DURATION_MS = 100;
const NEUTRAL_SCALE = 1;

const emit = defineEmits<{
  (e: "panned", value: boolean): void;
}>();

const currentScale = ref(NEUTRAL_SCALE);

const panX = ref(0);
const panY = ref(0);
const isPanning = ref(false);
const lastPointerPosition = ref<{ x: number; y: number } | null>(null);

const targetScale = defineModel<number>({ default: NEUTRAL_SCALE });

onMounted(() => {
  currentScale.value = minScale;
  targetScale.value = minScale;
});

const transformOrigin = "50% 50%";
const scaleTransform = computed(() => `scale(${currentScale.value})`);
const panTransform = computed(
  () => `translate(${panX.value}px, ${panY.value}px)`,
);

const lastZoomFocal = ref<{ x: number; y: number } | null>(null);

const getContainerRect = () =>
  containerRef.value?.getBoundingClientRect() ?? null;

const getMaxPan = (scale = currentScale.value) => {
  const rect = getContainerRect();
  if (!rect) return { maxX: 0, maxY: 0 };

  return {
    maxX: Math.max(0, (rect.width * scale - rect.width) / 2),
    maxY: Math.max(0, (rect.height * scale - rect.height) / 2),
  };
};

const applyZoomAt = (newScale: number, focalX: number, focalY: number) => {
  const rect = getContainerRect();
  if (!rect) return;

  const prevScale = currentScale.value;
  const clamped = Math.min(maxScale, Math.max(minScale, newScale));
  if (clamped === prevScale) return;

  const offsetFromCenterX = focalX - rect.width / 2;
  const offsetFromCenterY = focalY - rect.height / 2;

  panX.value += (prevScale - clamped) * offsetFromCenterX;
  panY.value += (prevScale - clamped) * offsetFromCenterY;

  currentScale.value = clamped;
  targetScale.value = clamped;

  clampPanToBounds();
};

const clampPanToBounds = () => {
  const { maxX, maxY } = getMaxPan();
  panX.value = Math.max(-maxX, Math.min(maxX, panX.value));
  panY.value = Math.max(-maxY, Math.min(maxY, panY.value));
};

const onWheelSpin = (e: WheelEvent) => {
  const rect = getContainerRect();
  if (!rect) return;

  const focalX = e.clientX - rect.left;
  const focalY = e.clientY - rect.top;
  const delta = e.deltaY < 0 ? 1 : -1;
  const desired = currentScale.value + delta * ZOOM_SPEED;
  applyZoomAt(desired, focalX, focalY);
};

const handleMouseHoverMove = useThrottleFn((e: MouseEvent) => {
  if (currentScale.value <= minScale) return;

  if (activePointers.value > 0 || isPanning.value) return;

  const rect = getContainerRect();
  if (!rect) return;

  const offsetFromCenterX = e.clientX - (rect.left + rect.width / 2);
  const offsetFromCenterY = e.clientY - (rect.top + rect.height / 2);
  const factor = 1 - currentScale.value; // negative when zoomed in

  panX.value = factor * offsetFromCenterX;
  panY.value = factor * offsetFromCenterY;
  clampPanToBounds();
}, HOVER_THROTTLE_MS);

watch(targetScale, (value) => {
  if (value === currentScale.value) return;

  const rect = getContainerRect();
  if (!rect) return;
  applyZoomAt(value, rect.width / 2, rect.height / 2);
});

const { onPointerDown, onPointerMove, onPointerUp } = usePinchGesture(
  containerRef,
  {
    minScale: () => minScale,
    maxScale: () => maxScale,
    currentScale: () => currentScale.value,
    onZoom: (scale) => {
      const rect = getContainerRect();
      if (!rect) return;

      const focalX = lastZoomFocal.value?.x ?? rect.width / 2;
      const focalY = lastZoomFocal.value?.y ?? rect.height / 2;
      applyZoomAt(scale, focalX, focalY);
    },
    onOriginChange: (xPercent, yPercent) => {
      const rect = getContainerRect();
      if (!rect) return;
      lastZoomFocal.value = {
        x: (xPercent / 100) * rect.width,
        y: (yPercent / 100) * rect.height,
      };
    },
  },
);

const activePointers = ref(0);
const hasPannedDuringInteraction = ref(false);

const handlePointerDown = (e: PointerEvent) => {
  activePointers.value++;
  onPointerDown(e);
  if (activePointers.value === 1 && currentScale.value > minScale) {
    isPanning.value = true;
    lastPointerPosition.value = { x: e.clientX, y: e.clientY };
    hasPannedDuringInteraction.value = false;
  }
};

const handlePointerMove = (e: PointerEvent) => {
  if (activePointers.value > 1) {
    e.preventDefault();
  }
  onPointerMove(e);

  if (
    activePointers.value === 1 &&
    isPanning.value &&
    lastPointerPosition.value
  ) {
    e.preventDefault();
    const deltaX = e.clientX - lastPointerPosition.value.x;
    const deltaY = e.clientY - lastPointerPosition.value.y;
    lastPointerPosition.value = { x: e.clientX, y: e.clientY };

    const rect = containerRef.value?.getBoundingClientRect();
    if (rect) {
      panX.value += deltaX;
      panY.value += deltaY;
      clampPanToBounds();
      if (!hasPannedDuringInteraction.value && (deltaX !== 0 || deltaY !== 0)) {
        hasPannedDuringInteraction.value = true;
        emit("panned", true);
      }
    }
  }
};

const handlePointerUp = (e: PointerEvent) => {
  activePointers.value = Math.max(0, activePointers.value - 1);
  onPointerUp(e);
  if (activePointers.value === 0) {
    isPanning.value = false;
    lastPointerPosition.value = null;
    if (hasPannedDuringInteraction.value) {
      setTimeout(() => emit("panned", false));
    }
    hasPannedDuringInteraction.value = false;
  }
};

watch(currentScale, () => clampPanToBounds());

const touchAction = computed(() => {
  if (activePointers.value > 1) return "none"; // multi-touch (pinch)

  if (currentScale.value > minScale) return "none"; // block page scroll when zoomed to allow panning

  return "pan-y"; // allow vertical page scroll at default zoom
});

// Animates the reset of zoom and pan to the neutral state simultaneously to prevent janky transitions
const animateReset = (
  duration = RESET_ANIMATION_DURATION_MS,
): Promise<void> => {
  const startScale = currentScale.value;
  const startPanX = panX.value;
  const startPanY = panY.value;
  const start = performance.now();

  return new Promise((resolve) => {
    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const nextScale = startScale + (minScale - startScale) * progress;
      currentScale.value = nextScale;
      targetScale.value = nextScale;
      panX.value = startPanX + (0 - startPanX) * progress;
      panY.value = startPanY + (0 - startPanY) * progress;
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        panX.value = 0;
        panY.value = 0;
        clampPanToBounds();
        resolve();
      }
    };
    requestAnimationFrame(step);
  });
};

defineExpose({ animateReset });
</script>

<template>
  <div
    ref="containerRef"
    class="overflow-hidden select-none size-full relative"
    :style="{ touchAction }"
    @wheel.prevent="onWheelSpin"
    @mousemove="handleMouseHoverMove"
    @pointerdown="handlePointerDown"
    @pointermove="handlePointerMove"
    @pointerup="handlePointerUp"
    @pointercancel="handlePointerUp"
    @pointerleave="handlePointerUp"
  >
    <div
      class="size-full"
      :style="{ transform: panTransform }"
    >
      <div :style="{ transform: scaleTransform, transformOrigin }">
        <slot />
      </div>
    </div>
  </div>
</template>
